import React from 'react';
import { BuildingOffice2Icon } from '@heroicons/react/20/solid';
import ChecklistMenu, { ChecklistMenuOption } from '@ui/menu/ChecklistMenu';
import Legend from '@ui/legend/Legend';
import { CompareItem, MAX_ITEMS } from './Compare.utilities';

type Props = {
	choices: CompareItem[],
	items: CompareItem[],
	onChange: (items: CompareItem[]) => void,
};

// The marks of the line "Compared": the series slots 2 … 7 of the chart tokens (chartTokens CHART_SLOTS), in the order of
// the items, as the lines of the chart and the key lines of the table.
const MARKS = ['bg-orange-600', 'bg-cyan-600', 'bg-pink-600', 'bg-amber-500', 'bg-violet-700', 'bg-lime-600'];

// "Compare · 4 of 6" (direction A, D17–D19): the list of All properties, the Property groups and the properties, with a
// search; a property with no reservations is off with its note (D14); at 6 the other choices are off (D2). The line
// "Compared" names what the comparison shows now. One tick adds or removes one item; the order of the items is the order
// of the ticks (T8). The last item cannot be unticked.
const CompareItemsMenu: React.FunctionComponent<Props> = ({ choices, items, onChange }) => {
	const options: ChecklistMenuOption[] = choices.map(c => ({
		id: c.id, label: c.label, detail: c.detail, searchText: `${c.label} ${c.detail}`,
		section: c.id === 'all' ? undefined : (c.id.startsWith('g:') ? 'Property groups' : 'Properties'),
		...(c.hasReservations ? {} : { disabled: true, note: 'No reservations' }),
	}));
	const selected = items.map(i => i.id);
	const toggle = (id: string) => {
		if (selected.includes(id)) onChange(items.filter(i => i.id !== id));
		else {
			const item = choices.find(c => c.id === id);
			if (item !== undefined) onChange([...items, item]);
		}
	};
	const full = items.length >= MAX_ITEMS;
	return <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
		<ChecklistMenu listLabel="What to compare" options={options} selected={selected} onToggle={toggle} max={MAX_ITEMS} min={1}
			search={{ placeholder: 'Search properties and groups', empty: 'No property or group matches' }}
			hint={`${items.length} of ${MAX_ITEMS} selected${full ? ' · the limit: untick one to add another' : ` · up to ${MAX_ITEMS}`}`}
			label={<><BuildingOffice2Icon aria-hidden="true" className="h-4 w-4 text-gray-400" />Compare<span className="font-normal text-gray-500">· {items.length} of {MAX_ITEMS}</span></>} />
		<Legend groups={[{ label: 'Compared', items: items.map((item, i) => ({ label: item.propertyGroupId !== null ? `${item.label} (${item.detail})` : item.label, markClassName: MARKS[i] })) }]} />
	</div>;
};

export default CompareItemsMenu;
